/**
 * CategorySelector Component
 * 
 * Checkbox list for WooCommerce product categories.
 * Fetches categories from API on mount and displays all as checkboxes.
 * 
 * Requirements: 6.1, 6.2
 */

import { useState, useEffect, useCallback } from 'react';
import { getProtectedClient } from '../api/client';

export interface Category {
  id: number;
  name: string;
  slug: string;
  parent: number;
  count: number;
}

export interface CategorySelectorProps {
  value: number[];
  onChange: (categoryIds: number[]) => void;
  disabled?: boolean;
}

export function CategorySelector({ value, onChange, disabled = false }: CategorySelectorProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const client = getProtectedClient();
        const response = await client.get<{ success: boolean; data: Category[] }>('/categories');
        
        if (response.data.success) {
          // Filter out "Uncategorized"
          const filtered = response.data.data.filter(
            cat => cat.name.toLowerCase() !== 'uncategorized' && cat.slug !== 'uncategorized'
          );
          setCategories(filtered);
        } else {
          setError('Failed to load categories');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load categories');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  /**
   * Toggle category selection
   */
  const toggleCategory = useCallback((categoryId: number) => {
    const isSelected = value.includes(categoryId);
    
    if (isSelected) {
      onChange(value.filter(id => id !== categoryId));
    } else {
      onChange([...value, categoryId]);
    }
  }, [value, onChange]);

  if (isLoading) {
    return (
      <div className="category-selector category-selector--loading">
        <span className="loading-text">Loading categories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="category-selector category-selector--error">
        <span className="error-text">{error}</span>
      </div>
    );
  }

  return (
    <div className="category-selector">
      <div className="category-checkbox-list">
        {categories.length === 0 ? (
          <span className="category-selector__no-results">No categories available</span>
        ) : (
          categories.map(category => {
            const isSelected = value.includes(category.id);
            return (
              <label key={category.id} className="category-checkbox-item">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleCategory(category.id)}
                  disabled={disabled}
                />
                <span className="category-name">{category.name}</span>
                {category.parent > 0 && (
                  <span className="category-parent-indicator"> (child)</span>
                )}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

export default CategorySelector;
